import { useMemo } from 'react'
import { buildRosterRows } from '../lib/roster'
import Portrait, { EmptyPortrait } from './Portrait'
import { displayPosition, positionFill } from './board/positions'
import type { DraftState, PlayerSummary } from '../types/protocol'

// The owner's roster as it actually stands: every slot from roster_config,
// filled or empty. The fill order comes from lib/roster.ts, which mirrors the
// server's RosterBuilder — so what shows here is the same assignment autopick
// used when it decided a position still fit, and the same one the results page
// will print.

interface Props {
  draft: DraftState
  teamId: number
  playersById: Map<number, PlayerSummary>
}

interface Slotted {
  id: number
  player_id: number | null
  position: string | null
  round: number
  overall: number
  player: PlayerSummary | null
}

export default function RosterPanel({ draft, teamId, playersById }: Props) {
  const rows = useMemo(() => {
    // Pick order is the roster builder's whole input, so sort before slotting.
    const picks: Slotted[] = draft.picks
      .filter((pick) => pick.team_id === teamId && pick.status === 'made')
      .sort((a, b) => a.overall - b.overall)
      .map((pick) => {
        const player = pick.player_id != null ? (playersById.get(pick.player_id) ?? null) : null
        return {
          id: pick.overall,
          player_id: pick.player_id,
          position: player?.position ?? null,
          round: pick.round,
          overall: pick.overall,
          player,
        }
      })

    return buildRosterRows(draft.roster_config, picks)
  }, [draft.picks, draft.roster_config, teamId, playersById])

  const filled = rows.filter((row) => row.pick).length

  return (
    <section className="panel p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="eyebrow">My roster</h2>
        <p className="font-mono text-[11px] text-ink-lo tabular-nums">
          {filled}/{rows.length}
        </p>
      </div>

      {/* Filled and empty rows use the same portrait box on purpose: a roster
          column that changed height as it filled would make the whole panel
          jump on every pick. */}
      <ul className="mt-3 space-y-1">
        {rows.map((row, index) => (
          <li
            key={`${row.slot}-${index}`}
            className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 ${
              row.pick?.player ? 'bg-ink/50' : 'bg-transparent'
            }`}
          >
            <span className="w-10 shrink-0 font-mono text-[10px] font-semibold tracking-wide text-ink-lo uppercase">
              {row.slot}
              {!row.configured && '*'}
            </span>
            {row.pick?.player ? (
              <>
                <Portrait
                  fullName={row.pick.player.full_name}
                  position={row.pick.player.position}
                  nflTeam={row.pick.player.nfl_team}
                  src={row.pick.player.portrait_url}
                  size="md"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm leading-tight text-ink-hi">
                    {row.pick.player.full_name}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5">
                    <span
                      className={`${positionFill(row.pick.player.position)} text-pos-ink rounded-sm px-1 font-mono text-[9px] font-semibold`}
                    >
                      {displayPosition(row.pick.player.position)}
                    </span>
                    {/* The jersey and the bye join this row rather than adding
                        one: a filled row and an empty row have to stay the same
                        height or the panel jumps on every pick. */}
                    <span className="font-mono text-[10px] text-ink-lo tabular-nums">
                      {row.pick.player.nfl_team ?? 'FA'}
                      {row.pick.player.jersey_number != null &&
                        ` · #${row.pick.player.jersey_number}`}
                      {row.pick.player.bye_week != null && ` · BYE ${row.pick.player.bye_week}`}{' '}
                      · R{row.pick.round}
                    </span>
                  </span>
                </span>
              </>
            ) : (
              <>
                <EmptyPortrait label={row.slot} size="md" />
                <span className="flex-1 text-sm text-ink-lo">Empty</span>
              </>
            )}
          </li>
        ))}
      </ul>

      {rows.some((row) => !row.configured) && (
        <p className="mt-2 text-xs text-ink-lo">
          * beyond the configured roster — kept visible so no pick disappears.
        </p>
      )}
    </section>
  )
}
